import { Box, Button, Stack, Typography } from '@dtx-company/design-system/src'
import { BuyPlanPageContentProps } from './BuyPlanPageContentProps'
import { BuyPlanPageContext } from './context/BuyPlanPageContext'
import { BuyPlanPageDivider } from './components/BuyPlanPageDivider/BuyPlanPageDivider'
import { BuyPlanPageExistingPaymentMethod } from './components/BuyPlanPageExistingPaymentMethod/BuyPlanPageExistingPaymentMethod'
import { BuyPlanPagePlanDetails } from './components/BuyPlanPagePlanDetails/BuyPlanPagePlanDetails'
import { BuyPlanPagePlanPicker } from './components/BuyPlanPagePlanPicker/BuyPlanPagePlanPicker'
import { BuyPlanPagePlanPromoCode } from './components/BuyPlanPagePromoCode/BuyPlanPagePromoCode'
import { BuyPlanPagePlanSummary } from './components/BuyPlanPagePlanSummary/BuyPlanPagePlanSummary'
import { DEFAULT_PLAN_PRICING_GROUP } from '@dtx-company/inter-app/src/hooks/usePlanPricingGroup/usePlanPricingGroup'
import { FlexibleBox } from '@app/common/src/components/FlexibleBox'
import { FullPageErrorFallback } from '@dtx-company/inter-app/src/components/PageErrorFallback'
import { Lock } from '@mui/icons-material'
import { LoggerErrorBoundary } from '@dtx-company/logger'
import { PaymentMethod } from '@stripe/stripe-js'
import { PlanInterval } from '@dtx-company/true-common/src/types/PlanInterval'
import { PlanPurchaseForm } from '@app/plans/src/PlanPurchaseForm/PlanPurchaseForm'
import { PlanPurchaseFormAddressElement } from '@app/plans/src/PlanPurchaseFormAddressElement/PlanPurchaseFormAddressElement'
import { PlanPurchaseFormPaymentElement } from '@app/plans/src/PlanPurchaseFormPaymentElement/PlanPurchaseFormPaymentElement'
import { PlanPurchaseFormSubmitButton } from '@app/plans/src/PlanPurchaseFormSubmitButton/PlanPurchaseFormSubmitButton'
import { PlanType } from '@dtx-company/true-common/src/types/planType'
import { Theme } from '@mui/material'
import { buyPlanPagePrismicDocuments } from './__mocks__/buyPlanPagePrismicDocuments'
import { fireAnalyticsEvent } from '@dtx-company/inter-app/src/event-tracking/helpers/fireAnalyticsEvent'
import { getConversionSource } from '@app/common/src/components/PlanPurchaseExperience/utils/getConversionSource'
import { getPlanTypeName } from '@app/common/src/utils/getPlanTypeName'
import { getPlans } from '@dtx-company/inter-app/src/api/subscription-service/getPlans'
import { isOrgPlan } from '@app/common/src/utils/isOrgPlan'
import { isPilotPlanType } from '@app/common/src/utils/isPilotPlanType'
import { sendSuccessNotification } from '@dtx-company/inter-app/src/utils/notifications'
import { useAPISWR } from '@dtx-company/inter-app/src/hooks/useAPISWR/useAPISWR'
import { useAuthState } from '@dtx-company/inter-app/src/hooks/useAuthState'
import { useAvailablePlanExtrasSWR } from '@app/common/src/hooks/useAvailablePlanExtrasSWR'
import { useAvailablePlansSWR } from '@app/common/src/hooks/useAvailablePlansSWR'
import { useBillingPlanType } from '@app/common/src/hooks/useBillingPlanType/useBillingPlanType'
import { useBuyPlanPageAdditionalSeats } from './useBuyPagePlanAdditionalSeats'
import { useBuyPlanPageOtherPlanExtras } from './useBuyPagePlanOtherPlanExtras'
import { useBuyPlanPageQuery } from './routing/useBuyPlanPageQuery'
import { useCohortLimits } from '@dtx-company/inter-app/src/hooks/cohorts/useCohortLimits'
import { useEffect, useMemo, useState } from 'react'
import { useFlowRouter } from '@dtx-company/inter-app/src/hooks/useFlowRouter'
import { useInternalPlanPurchaseProviderContextUpdate } from '../../../shared/plans/src/purchase/PlanPurchaseProvider/context/useInternalPlanPurchaseProviderContextUpdate'
import { usePlanPurchaseExperienceSubscriptionInfo } from '@app/common/src/components/PlanPurchaseExperience/hooks/usePlanPurchaseExperienceSubscriptionInfo'
import { usePlanPurchasePromoCode } from '@app/plans/src/PlanPurchaseForm/hooks/usePlanPurchasePromoCode'
import { usePlanPurchaseProviderContext } from '@app/plans/src/purchase/usePlanPurchaseProviderContext/usePlanPurchaseProviderContext'
import { usePrismicDocumentInterpolation } from '@app/prismic/src/hooks/usePrismicDocumentInterpolation/usePrismicDocumentInterpolation'
import styled from '@emotion/styled'

const _FlexBox = styled(Box)`
  display: flex;
  flex-flow: column nowrap;
  flex: 0 0 auto;

  &:last-child {
    /* last one fills vertical space so right column bg color can fill */
    flex: 1 0 auto;
  }
`

const _FlexLeft = styled(Box)``
const _FlexRight = styled(Box)``

const _ContentBox = styled(Box)`
  flex: 1 0 auto;
  padding: 0 16px;
  width: 100%;
`

const _ContentHeadingBox = styled(Box)`
  align-items: center;
  display: flex;
  flex-flow: row nowrap;
  justify-content: space-between;
  margin: 48px 0 16px 0;
`

const _MainHeadingBox = styled(Box)`
  margin-top: 48px;
`

const _MobileSpacerBox = styled(Box)`
  padding-bottom: 64px;
`

const _DesktopSpacerBox = styled(Box)`
  display: none;
  padding-bottom: 64px;
`

const _RootBox = styled(FlexibleBox)`
  display: flex;
  flex: 1 0 auto;
  flex-flow: column nowrap;
  justify-content: flex-start;
  height: 100%;
  margin: 0;

  ${_FlexLeft},
  ${_FlexRight} {
    align-items: center;
    display: flex;
    flex-flow: column nowrap;
  }

  &.twoColumn {
    ${_MobileSpacerBox} {
      display: none;
    }

    ${_DesktopSpacerBox} {
      display: block;
    }

    ${_FlexBox} {
      flex-flow: row nowrap;
    }

    ${_FlexLeft},
    ${_FlexRight} {
      flex: 1 1 50%;
      order: 0;
    }

    ${_FlexLeft} {
      align-items: flex-end;
    }

    ${_FlexRight} {
      align-items: flex-start;
      background-color: ${({ theme }) => (theme as Theme).palette.surface[2]};
    }

    ${_ContentBox} {
      max-width: ${525 + 48}px;
      padding: 0 24px;
    }
  }

  &.twoColumnWide {
    ${_ContentBox} {
      max-width: ${525 + 128}px;
      padding: 0 64px;
    }
  }
`

function InnerBuyPlanPageContent(props: BuyPlanPageContentProps): JSX.Element | null {
  const { promoCode: initialPromoCode } = useBuyPlanPageQuery()

  const { isLoaded: planPurchaseProviderContextIsLoaded, selectedPlan } =
    usePlanPurchaseProviderContext()

  const updateContext = useInternalPlanPurchaseProviderContextUpdate()

  const planType = selectedPlan?.type || PlanType.PRO
  const pricingGroup = selectedPlan?.group || DEFAULT_PLAN_PRICING_GROUP

  const prismicDocument = buyPlanPagePrismicDocuments[planType]
  const authState = useAuthState()
  const flowRouter = useFlowRouter()
  const [defaultPlanInterval, setDefaultPlanInterval] = useState<PlanInterval>()
  const [validPromoCode, setValidPromoCode] = useState<string | undefined>(undefined)
  const [existingPaymentMethod, setExistingPaymentMethod] = useState<PaymentMethod | undefined>(
    undefined
  )

  const [currentSubscriptionHasLoaded, setCurrentSubscriptionHasLoaded] = useState(false)
  const { currentSubscriptionInfoData, isLoading: currentSubscriptionIsLoading } =
    usePlanPurchaseExperienceSubscriptionInfo()
  const currentPlan = currentSubscriptionInfoData?.plan
  const billingPlanType = useBillingPlanType()
  const isPilot = billingPlanType && isPilotPlanType(billingPlanType)

  const planTypeName = getPlanTypeName(planType)
  const cohortLimits = useCohortLimits(planType, true)
  const planFlowcodeLimit = String(cohortLimits.codeLimit)
  const planFlowpageLimit = String(cohortLimits.pageLimit)
  const conversionSource = getConversionSource(flowRouter)

  async function handleSuccessfulCheckout(): Promise<void> {
    if (document?.data.success_snackbar_text) {
      sendSuccessNotification(document.data.success_snackbar_text)
    }

    await props.onSuccessfulCheckout()
  }

  const {
    code: promoCode,
    isLoading: promoCodeLoading,
    isValid: isPromoValid,
    plans: promoCodePlanIds
  } = usePlanPurchasePromoCode(initialPromoCode)

  const availablePlansFromAPISWR = useAPISWR(
    getPlans,
    { filter: { groupEq: pricingGroup } },
    {
      revalidateIfStale: false,
      revalidateOnFocus: false,
      revalidateOnMount: true
    }
  )

  const availablePlans = useMemo(() => {
    return availablePlansFromAPISWR.data?.filter(plan => plan.type === planType) || []
  }, [availablePlansFromAPISWR.data, planType])

  const {
    planExtra: planExtraAdditionalSeats,
    setAdditionalSeatsQuantity,
    planExtraSeatsDetails,
    numBaseSeats,
    pricePerSeat
  } = useBuyPlanPageAdditionalSeats({
    planType,
    currentSubscriptionInfoData,
    selectedPlan
  })

  const { restPlanExtraDetails } = useBuyPlanPageOtherPlanExtras({
    planType,
    currentSubscriptionInfoData,
    selectedPlan
  })

  // plan extra details is not the same as a plan extra, its just the plan extra id and quantity
  // that gets passed to create/update a subscription
  const planExtraDetails = [
    ...(planExtraSeatsDetails ? [planExtraSeatsDetails] : []),
    ...restPlanExtraDetails
  ]

  const availablePlansSWR = useAvailablePlansSWR(
    () => {
      if (!authState.token) return
      if (!pricingGroup) return
      return {
        token: authState.token,
        filter: {
          planPricingGroup: pricingGroup
        },
        include: {
          plan: true
        }
      }
    },
    {
      revalidateIfStale: false,
      revalidateOnFocus: false,
      revalidateOnMount: true
    }
  )
  const selectedAvailablePlan = useMemo(() => {
    if (!selectedPlan) return
    if (!availablePlansSWR.data) return
    return availablePlansSWR.data.find((availablePlan: { planId: string }) => {
      return availablePlan.planId === selectedPlan.id
    })
  }, [selectedPlan, availablePlansSWR.data])

  const availablePlanExtrasSWR = useAvailablePlanExtrasSWR(
    () => {
      if (!authState.token) return
      if (!selectedAvailablePlan) return
      return {
        token: authState.token,
        availablePlanId: selectedAvailablePlan.availablePlanId,
        include: {
          planExtra: true
        }
      }
    },
    {
      revalidateIfStale: false,
      revalidateOnFocus: false,
      revalidateOnMount: true
    }
  )
  const showPlanExtras = availablePlanExtrasSWR.data && availablePlanExtrasSWR.data.length > 0
  const showOrgName = isOrgPlan(planType) && (!authState.jwt?.org?.name || isPilot)
  const existingOrgName = authState.jwt?.org?.name

  // Determine default plan interval based on which one the promo code query param applies to
  useEffect(() => {
    if ((initialPromoCode && promoCodeLoading) || availablePlansFromAPISWR.isLoading) {
      return
    }

    const yearlyPlanInterval = PlanInterval.YEARLY
    const monthlyPlanInterval = PlanInterval.MONTHLY

    // Prefer to match a yearly plan
    const yearlyAvailablePlans = availablePlans.filter(plan => plan.interval === yearlyPlanInterval)
    const matchingYearlyPlan = yearlyAvailablePlans.find(plan => promoCodePlanIds.includes(plan.id))
    if (matchingYearlyPlan) {
      setDefaultPlanInterval(yearlyPlanInterval)
      updateContext({
        planInterval: yearlyPlanInterval
      })
      return
    }

    // If not, then check for monthly
    const monthlyAvailablePlans = availablePlans.filter(
      plan => plan.interval === monthlyPlanInterval
    )
    const matchingMonthlyPlan = monthlyAvailablePlans.find(plan =>
      promoCodePlanIds.includes(plan.id)
    )
    if (matchingMonthlyPlan) {
      setDefaultPlanInterval(monthlyPlanInterval)
      updateContext({
        planInterval: monthlyPlanInterval
      })
      return
    }

    // If nothing else, default to yearly
    setDefaultPlanInterval(yearlyPlanInterval)
    updateContext({
      planInterval: yearlyPlanInterval
    })
  }, [
    initialPromoCode,
    promoCodeLoading,
    availablePlansFromAPISWR.isLoading,
    promoCodePlanIds,
    availablePlans,
    updateContext,
    planType,
    pricingGroup
  ])

  useEffect(() => {
    if (isPromoValid && defaultPlanInterval) {
      setValidPromoCode(promoCode)
    }
  }, [currentPlan, defaultPlanInterval, planType, promoCode, isPromoValid])

  const document = usePrismicDocumentInterpolation(
    () => {
      // Wait for auth to load so that it doesn't flash the default cohort limits
      // before getting the user's proper cohort
      if (!authState.isAuthChecked) return
      return prismicDocument || props.fallbackBuyPlanPageDocument
    },
    {
      planTypeName,
      planFlowcodeLimit,
      planFlowpageLimit,
      numBaseSeats,
      pricePerSeat
    },
    { team: 'monetization' }
  )

  // BUGFIX: Current subscription reloads when the JWT updates after purchase, but the
  // page does not need to reload and must not re-trigger `userStartedProConversion`
  useEffect(() => {
    if (!currentSubscriptionIsLoading) {
      setCurrentSubscriptionHasLoaded(true)
    }
  }, [currentSubscriptionIsLoading])

  const canViewSavedCard = authState.hasOperation('view:saved_card_payment')
  // Determine if should show existing card by default
  useEffect(() => {
    if (!currentSubscriptionInfoData) return
    if (!authState.isAuthChecked) return
    setExistingPaymentMethod(
      canViewSavedCard ? currentSubscriptionInfoData?.paymentMethod : undefined
    )
  }, [
    currentSubscriptionInfoData,
    canViewSavedCard,
    authState.isAuthChecked,
    currentSubscriptionInfoData?.paymentMethod
  ])

  // There's a bug where SWR stuff reloads and puts this back into a loading state,
  // and then userStartedProConversion ends up firing twice when it's opened more than
  // once from an upsell modal so this is to fix that by only allowing to show
  // a loading state once, thus PlanPurchaseForm only mounts once.
  const [everLoaded, setEverLoaded] = useState(false)
  useEffect(() => {
    if (planPurchaseProviderContextIsLoaded) {
      setEverLoaded(true)
    }
  }, [planPurchaseProviderContextIsLoaded])

  // Wait for page to load before rendering anything
  // TODO: improve loading indication
  if (
    !document ||
    !defaultPlanInterval ||
    !currentSubscriptionHasLoaded ||
    !everLoaded ||
    !selectedPlan
  ) {
    return <></>
  }

  const { data } = document

  return (
    <BuyPlanPageContext.Provider
      value={{
        document,
        defaultPlanInterval,
        existingPaymentMethod,
        setAdditionalSeatsQuantity,
        planExtraSeatsDetails,
        planExtraDetails,
        planExtraAdditionalSeats,
        showPlanExtras,
        showOrgName
      }}
    >
      <_RootBox widthClasses={{ twoColumn: 768, twoColumnWide: 1440 }}>
        <PlanPurchaseForm
          heapId="BuyPlanPage"
          lockSelected={props.lockSelected}
          lockPrismicId={props.lockPrismicId}
          token={authState.token}
          currentPlanId={currentPlan?.id}
          defaultValues={{
            planInterval: defaultPlanInterval,
            promoCode: validPromoCode
          }}
          validationErrors={{
            planIntervalRequired: document.data.val_plan_interval_required,
            orgNameRequired: document.data.org_name_required,
            orgNameMinMaxLength: document.data.org_name_min_max_length
          }}
          style={{
            display: 'flex',
            flexFlow: 'column nowrap',
            flex: '1 1 auto'
          }}
          conversionSource={conversionSource}
          onSuccessfulCheckout={handleSuccessfulCheckout}
          // just seats right now, update if/when we support more plan extras again
          planExtraDetails={planExtraDetails}
          existingPaymentMethod={existingPaymentMethod}
          existingOrgName={existingOrgName}
        >
          <_FlexBox>
            <_FlexLeft>
              <_ContentBox>
                {data.page_heading && (
                  <_MainHeadingBox>
                    <Typography
                      component="h1"
                      variant={{
                        xs: 'headlineMedium',
                        lg: 'headlineLarge',
                        xl: 'headlineXLarge'
                      }}
                      color="#111111"
                    >
                      {data.page_heading}
                    </Typography>
                  </_MainHeadingBox>
                )}
              </_ContentBox>
            </_FlexLeft>
            <_FlexRight></_FlexRight>
          </_FlexBox>
          <_FlexBox>
            <_FlexLeft>
              <_ContentBox>
                <BuyPlanPagePlanPicker />
                {data.payment_heading && (
                  <_ContentHeadingBox>
                    <Typography component="h2" variant="headlineXSmall">
                      {data.payment_heading}
                    </Typography>
                    {existingPaymentMethod
                      ? data.change_existing_payment_button && (
                          <Button
                            variant="text"
                            size="large"
                            onClick={() => {
                              setExistingPaymentMethod(undefined)
                              fireAnalyticsEvent('BuyPlanPage_EditExistingPaymentButton_Clicked')
                            }}
                          >
                            <Box color="onSurface.secondary">
                              {data.change_existing_payment_button}
                            </Box>
                          </Button>
                        )
                      : data.secure_form_label && (
                          <Typography component="span" variant="subtitleMedium">
                            <span
                              style={{
                                display: 'flex',
                                flexFlow: 'row nowrap',
                                alignItems: 'center',
                                fontSize: '0.8em'
                              }}
                            >
                              <Lock
                                sx={{ fontSize: '1.2em', marginLeft: '32px', marginRight: '8px' }}
                              />
                              {data.secure_form_label}
                            </span>
                          </Typography>
                        )}
                  </_ContentHeadingBox>
                )}
                {existingPaymentMethod ? (
                  <BuyPlanPageExistingPaymentMethod />
                ) : (
                  <Stack gap={2}>
                    <PlanPurchaseFormPaymentElement heapId="BuyPlanPage_PaymentElement" />
                    <PlanPurchaseFormAddressElement heapId="BuyPlanPage_AddressElement" />
                  </Stack>
                )}
                <BuyPlanPagePlanPromoCode />
                <_DesktopSpacerBox />
              </_ContentBox>
            </_FlexLeft>
            <_FlexRight>
              <_ContentBox>
                {data.summary_heading && (
                  <_ContentHeadingBox>
                    <Typography component="h2" variant="headlineXSmall" color="black">
                      {data.summary_heading}
                    </Typography>
                  </_ContentHeadingBox>
                )}
                <BuyPlanPagePlanDetails />
                <BuyPlanPageDivider sx={{ my: 4 }} />
                <BuyPlanPagePlanSummary />
                <PlanPurchaseFormSubmitButton
                  variant="contained"
                  color="tertiary"
                  sx={{ mt: 4 }}
                  fullWidth
                >
                  {data.purchase_cta}
                </PlanPurchaseFormSubmitButton>
                <_DesktopSpacerBox />
              </_ContentBox>
            </_FlexRight>
            <_MobileSpacerBox />
            {/* NOTE: _FlexBox must be last child to fill height */}
          </_FlexBox>
        </PlanPurchaseForm>
      </_RootBox>
    </BuyPlanPageContext.Provider>
  )
}

export function BuyPlanPageContent(props: BuyPlanPageContentProps): JSX.Element | null {
  return (
    <LoggerErrorBoundary options={{ team: 'monetization' }} fallback={<FullPageErrorFallback />}>
      <InnerBuyPlanPageContent {...props} />
    </LoggerErrorBoundary>
  )
}
